import { useState } from "react";

const FLOW_URLS = {
  selfReset: "https://d50fd29ff78aedaebc0560918946a0.1e.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/a3994d2fbc474022995a11f4d19f3f66/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=hfo2zWKE_cyqe1Wmu7vGi09hMOjag1gFG2ZFkXuPHiM",
  managerReset: "https://d50fd29ff78aedaebc0560918946a0.1e.environment.api.powerplatform.com:443/powerautomate/automations/direct/workflows/c9f5a454da7c47528d43f0f0c07613e5/triggers/manual/paths/invoke?api-version=1&sp=%2Ftriggers%2Fmanual%2Frun&sv=1.0&sig=HnmfEuoIrUDzEFyk0qi64SsGknNT5y3_Fm57NRd0n2c",
};

const isValidUpn = (v) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

function Spinner() {
  return (
    <span style={{
      display: "inline-block", width: 16, height: 16,
      border: "2px solid rgba(255,255,255,0.35)", borderTopColor: "#fff",
      borderRadius: "50%", animation: "spin 0.7s linear infinite", verticalAlign: "middle", marginRight: 8
    }} />
  );
}

function StatusMsg({ type, text }) {
  if (!text) return null;
  const colors = {
    success: { bg: "#e8f5e9", border: "#4caf50", color: "#1b5e20" },
    error: { bg: "#fce4ec", border: "#e53935", color: "#7f0000" },
    loading: { bg: "#e3f2fd", border: "#1976d2", color: "#0d3c61" },
  };
  const c = colors[type] || colors.loading;
  return (
    <div style={{ marginTop: 14, padding: "11px 14px", borderRadius: 8, fontSize: 13, fontWeight: 500, background: c.bg, border: `1px solid ${c.border}`, color: c.color }}>
      {text}
    </div>
  );
}

function Card({ children, accent }) {
  return (
    <div style={{
      background: "#fff", borderRadius: 14, padding: 28,
      boxShadow: "0 1px 4px rgba(0,0,0,0.07), 0 6px 20px rgba(0,0,0,0.06)",
      borderTop: `3px solid ${accent}`, flex: "1 1 320px", maxWidth: 460, minWidth: 290,
    }}>
      {children}
    </div>
  );
}

function Input({ label, placeholder, value, onChange, disabled }) {
  return (
    <div style={{ marginBottom: 16 }}>
      <label style={{ display: "block", fontSize: 12, fontWeight: 700, color: "#555", marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</label>
      <input
        type="email" placeholder={placeholder} value={value}
        onChange={e => onChange(e.target.value)} disabled={disabled}
        style={{
          width: "100%", padding: "10px 12px", border: "1.5px solid #ddd",
          borderRadius: 8, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
          background: disabled ? "#f9f9f9" : "#fff", color: "#111", outline: "none",
        }}
      />
    </div>
  );
}

function Btn({ onClick, disabled, loading, done, accent, children }) {
  return (
    <button onClick={onClick} disabled={disabled}
      style={{
        width: "100%", padding: "12px 0", border: "none", borderRadius: 8,
        background: disabled ? "#bbb" : accent, color: "#fff",
        fontSize: 14, fontWeight: 700, cursor: disabled ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: 6,
        transition: "opacity 0.15s",
      }}>
      {loading && <Spinner />}
      {children}
    </button>
  );
}

function SelfReset() {
  const [upn, setUpn] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  const reset = async () => {
    setState("loading"); setMsg("Resetting your session...");
    try {
      const r = await fetch(FLOW_URLS.selfReset, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ userUpn: upn.trim() }),
      });
      if (r.ok || r.status === 200) {
        setState("success");
        setMsg("Done. Wait about 1 minute, then reconnect to your VDI.");
      } else { throw new Error(); }
    } catch {
      setState("error"); setMsg("Something went wrong. Try again or contact the helpdesk.");
    }
  };

  const ready = isValidUpn(upn) && state !== "loading" && state !== "success";

  return (
    <Card accent="#1565c0">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#1565c0", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>For agents</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#111", marginBottom: 4 }}>Reset my session</div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
          Black screen or can't connect? Enter your SAC email and reset your own session.
        </div>
      </div>
      <Input label="Your SAC email" placeholder="you@sac.shiseido.com" value={upn} onChange={v => { setUpn(v); if (state !== "idle") { setState("idle"); setMsg(""); } }} disabled={state === "loading" || state === "success"} />
      <Btn onClick={reset} disabled={!ready} loading={state === "loading"} accent="#1565c0">
        {state === "success" ? "Session reset" : state === "loading" ? "Resetting..." : "Reset my session"}
      </Btn>
      {state === "success" && (
        <button onClick={() => { setState("idle"); setMsg(""); setUpn(""); }}
          style={{ width: "100%", marginTop: 10, padding: "9px 0", border: "1px solid #ddd", borderRadius: 8, background: "transparent", color: "#666", fontSize: 13, cursor: "pointer" }}>
          Reset again
        </button>
      )}
      <StatusMsg type={state === "idle" ? null : state} text={msg} />
    </Card>
  );
}

function ManagerReset() {
  const [requestorUpn, setRequestorUpn] = useState("");
  const [targetUpn, setTargetUpn] = useState("");
  const [state, setState] = useState("idle");
  const [msg, setMsg] = useState("");

  const reset = async () => {
    setState("loading"); setMsg(`Resetting session for ${targetUpn.trim()}...`);
    try {
      const r = await fetch(FLOW_URLS.managerReset, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ requestorUpn: requestorUpn.trim(), targetUpn: targetUpn.trim() }),
      });
      if (r.ok || r.status === 200) {
        setState("success");
        setMsg(`Done. Tell ${targetUpn.trim()} to wait ~1 minute then reconnect.`);
        setTargetUpn("");
      } else if (r.status === 403) {
        setState("error"); setMsg("Access denied. Your account is not authorized to reset other users.");
      } else { throw new Error(); }
    } catch (e) {
      if (state !== "error") { setState("error"); setMsg("Something went wrong. Check the emails and try again."); }
    }
  };

  const ready = isValidUpn(requestorUpn) && isValidUpn(targetUpn) && state !== "loading";

  return (
    <Card accent="#6b21a8">
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: "#6b21a8", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>For supervisors & helpdesk</div>
        <div style={{ fontSize: 18, fontWeight: 800, color: "#111", marginBottom: 4 }}>Reset someone else's session</div>
        <div style={{ fontSize: 13, color: "#666", lineHeight: 1.5 }}>
          Enter your SAC email and the agent's SAC email. You must be in the authorized group.
        </div>
      </div>
      <Input label="Your SAC email" placeholder="supervisor@sac.shiseido.com" value={requestorUpn}
        onChange={v => { setRequestorUpn(v); if (state !== "idle") { setState("idle"); setMsg(""); } }}
        disabled={state === "loading"} />
      <Input label="Agent's SAC email" placeholder="agent@sac.shiseido.com" value={targetUpn}
        onChange={v => { setTargetUpn(v); if (state !== "idle") { setState("idle"); setMsg(""); } }}
        disabled={state === "loading"} />
      <Btn onClick={reset} disabled={!ready} loading={state === "loading"} accent="#6b21a8">
        {state === "loading" ? "Resetting..." : "Reset their session"}
      </Btn>
      {state === "success" && (
        <button onClick={() => { setState("idle"); setMsg(""); }}
          style={{ width: "100%", marginTop: 10, padding: "9px 0", border: "1px solid #ddd", borderRadius: 8, background: "transparent", color: "#666", fontSize: 13, cursor: "pointer" }}>
          Reset another agent
        </button>
      )}
      <StatusMsg type={state === "idle" ? null : state} text={msg} />
    </Card>
  );
}

export default function App() {
  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        body { font-family: 'Inter', sans-serif; background: #f4f6fb; }
        @keyframes spin { to { transform: rotate(360deg); } }
        input:focus { border-color: #1565c0 !important; box-shadow: 0 0 0 3px rgba(21,101,192,0.12); }
        button:not(:disabled):hover { opacity: 0.88; }
      `}</style>
      <div style={{ minHeight: "100vh", padding: "40px 20px" }}>
        <div style={{ maxWidth: 980, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 40 }}>
            <div style={{ display: "inline-block", background: "#fff", border: "1px solid #e0e0e0", borderRadius: 999, padding: "5px 16px", fontSize: 12, fontWeight: 700, color: "#555", letterSpacing: "0.05em", textTransform: "uppercase", marginBottom: 16 }}>
              Shiseido Americas IT
            </div>
            <h1 style={{ fontSize: 30, fontWeight: 800, color: "#111", marginBottom: 10, letterSpacing: "-0.5px" }}>
              Qualfon / Shiseido VDI Session Reset
            </h1>
            <p style={{ fontSize: 15, color: "#666", maxWidth: 480, margin: "0 auto" }}>
              Black screen or can't connect to your virtual desktop?<br />
              Reset your session below.
            </p>
          </div>
          <div style={{ display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap" }}>
            <SelfReset />
            <ManagerReset />
          </div>
          <div style={{ textAlign: "center", marginTop: 36, fontSize: 12, color: "#aaa" }}>
            Session will be fully cleared within ~1 minute of reset. Contact support in Teams if issues persist.
          </div>
        </div>
      </div>
    </>
  );
}
